import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Sort, type SortCallbackData, type SortOption } from '@shared/components/ui/Sort';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { faGift, faSearch, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@shared/utils/utils';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateMarketingRuleConditionPayload,
  LocalMarketingRule,
  PackagingOptionItem,
  PackagingRuleField,
  PackagingRuleOperator,
} from '../../models/PackagingRulesets';

interface ConditionFormRow {
  id: string;
  joinWithPrevious: 'AND' | 'OR';
  field: PackagingRuleField;
  operator: PackagingRuleOperator;
  value: string;
  boolValue: boolean;
}

export interface EditMarketingRuleSubmitInput {
  ruleId: number;
  name: string;
  priority: number;
  active: boolean;
  isAlways: boolean;
  conditions?: CreateMarketingRuleConditionPayload[];
  conditionSummary?: string;
  actions: { packaging_item_id: number; quantity: number }[];
}

export interface EditMarketingRuleModalRef {
  open: (rule: LocalMarketingRule) => void;
  close: () => void;
}

interface EditMarketingRuleModalProps {
  catalog: PackagingOptionItem[];
  countries: { id: number; name: string; iso2: string }[];
  isSubmitting?: boolean;
  onSubmit: (payload: EditMarketingRuleSubmitInput) => Promise<void> | void;
}

const createConditionRow = (joinWithPrevious: 'AND' | 'OR' = 'AND'): ConditionFormRow => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  joinWithPrevious,
  field: 'total_amount',
  operator: '>',
  value: '',
  boolValue: true,
});

// Normaliza aliases del backend para mostrar campos entendibles en UI.
const normalizeFieldForUi = (field: string): PackagingRuleField => {
  if (field === 'order_total') return 'total_amount';
  return (field as PackagingRuleField) ?? 'total_amount';
};

const conditionToRow = (
  condition: CreateMarketingRuleConditionPayload,
  prevGroupNo: number | null,
): ConditionFormRow => {
  const groupNo = condition.group_no ?? 0;
  const isBoolean = condition.value_type === 'boolean';
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    joinWithPrevious: prevGroupNo === null ? 'AND' : groupNo !== prevGroupNo ? 'OR' : 'AND',
    field: normalizeFieldForUi(condition.field),
    operator: condition.operator,
    value: condition.value_number !== undefined ? String(condition.value_number) : (condition.value_string ?? ''),
    boolValue: isBoolean ? Boolean(condition.value_boolean) : true,
  };
};

export const EditMarketingRuleModal = forwardRef<EditMarketingRuleModalRef, EditMarketingRuleModalProps>(
  ({ catalog, countries, isSubmitting, onSubmit }, ref) => {
    const { t } = useTranslation('packagingRulesetsModule');
    const modalRef = useRef<ModalCenterRef>(null);

    const [ruleId, setRuleId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [priority, setPriority] = useState(100);
    const [activeRule, setActiveRule] = useState(true);
    const [alwaysInclude, setAlwaysInclude] = useState(true);
    const [conditionRows, setConditionRows] = useState<ConditionFormRow[]>([createConditionRow()]);

    const [quantities, setQuantities] = useState<Record<number, string>>({});
    const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [catalogSort, setCatalogSort] = useState<'id_desc' | 'id_asc' | 'name_asc' | 'name_desc'>('id_desc');
    const [hasCatalogSortSelection, setHasCatalogSortSelection] = useState(false);
    const [visibleCatalogCount, setVisibleCatalogCount] = useState(6);
    const qtyInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    useImperativeHandle(ref, () => ({
      open: (rule: LocalMarketingRule) => {
        setRuleId(rule.ruleId);
        setName(rule.name);
        setPriority(rule.priority);
        setActiveRule(rule.active);
        setAlwaysInclude(rule.isAlways);

        const apiConditions = rule.conditions ?? [];
        if (apiConditions.length > 0) {
          let prevGroupNo: number | null = null;
          const rows = apiConditions.map((condition) => {
            const row = conditionToRow(condition, prevGroupNo);
            prevGroupNo = condition.group_no ?? 0;
            return row;
          });
          setConditionRows(rows);
        } else {
          setConditionRows([createConditionRow()]);
        }

        const selectedIds = rule.actions.map((action) => action.packaging_item_id);
        const qtyMap: Record<number, string> = {};
        rule.actions.forEach((action) => {
          qtyMap[action.packaging_item_id] = String(action.quantity || 1);
        });
        setSelectedMaterialIds(selectedIds);
        setQuantities(qtyMap);

        setCatalogSearch('');
        setCatalogSort('id_desc');
        setHasCatalogSortSelection(false);
        setVisibleCatalogCount(6);

        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const fieldOptions = [
      { value: 'total_amount', label: t('specialRulesModal.fields.total_amount') },
      { value: 'country', label: t('specialRulesModal.fields.country') },
      { value: 'fragile', label: t('specialRulesModal.fields.fragile') },
      { value: 'campaign', label: t('specialRulesModal.fields.campaign') },
    ];

    const operatorOptions = [
      { value: '=', label: t('specialRulesModal.operators.eq') },
      { value: '!=', label: t('specialRulesModal.operators.neq') },
      { value: '>', label: t('specialRulesModal.operators.gt') },
      { value: '<', label: t('specialRulesModal.operators.lt') },
      { value: '>=', label: t('specialRulesModal.operators.gte') },
      { value: '<=', label: t('specialRulesModal.operators.lte') },
      { value: 'contains', label: t('specialRulesModal.operators.contains') },
    ];

    const isNumericField = (row: ConditionFormRow) => row.field === 'total_amount';
    const isBooleanField = (row: ConditionFormRow) => row.field === 'fragile';
    const isCountryField = (row: ConditionFormRow) => row.field === 'country';

    const updateConditionRow = (id: string, updater: (row: ConditionFormRow) => ConditionFormRow) => {
      setConditionRows((prev) => prev.map((row) => (row.id === id ? updater(row) : row)));
    };

    const handleFieldChange = (id: string, newField: string) => {
      updateConditionRow(id, (row) => ({
        ...row,
        field: newField as PackagingRuleField,
        value: '',
        boolValue: true,
        operator: newField === 'total_amount' ? '>' : '=',
      }));
    };

    const countryOptions = countries.map((c) => ({ value: c.iso2, label: c.name }));

    const parseSortValue = (sort: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => {
      if (sort === 'id_asc') return { field: 'id', direction: 'asc' as const };
      if (sort === 'name_asc') return { field: 'name', direction: 'asc' as const };
      if (sort === 'name_desc') return { field: 'name', direction: 'desc' as const };
      return { field: 'id', direction: 'desc' as const };
    };

    const toSortValue = (f: string, direction: 'asc' | 'desc'): 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc' => {
      if (f === 'id' && direction === 'asc') return 'id_asc';
      if (f === 'name' && direction === 'asc') return 'name_asc';
      if (f === 'name' && direction === 'desc') return 'name_desc';
      return 'id_desc';
    };

    const catalogSortOptions = useMemo(
      (): SortOption[] => [
        { field: 'id', direction: 'desc', label: 'Mas nuevo a mas viejo' },
        { field: 'id', direction: 'asc', label: 'Mas viejo a mas nuevo' },
        { field: 'name', direction: 'asc', label: 'Alfabetico A-Z' },
        { field: 'name', direction: 'desc', label: 'Alfabetico Z-A' },
      ],
      [],
    );

    const filteredAndSortedCatalog = useMemo(() => {
      const search = catalogSearch.trim().toLowerCase();
      let result = search ? catalog.filter((i) => i.name.toLowerCase().includes(search)) : [...catalog];
      if (catalogSort === 'name_asc') result = result.sort((a, b) => a.name.localeCompare(b.name));
      else if (catalogSort === 'name_desc') result = result.sort((a, b) => b.name.localeCompare(a.name));
      else if (catalogSort === 'id_asc') result = result.sort((a, b) => a.id - b.id);
      else result = result.sort((a, b) => b.id - a.id);
      return result;
    }, [catalog, catalogSearch, catalogSort]);

    const visibleCatalogItems = useMemo(
      () => filteredAndSortedCatalog.slice(0, visibleCatalogCount),
      [filteredAndSortedCatalog, visibleCatalogCount],
    );
    const hasMoreCatalogItems = filteredAndSortedCatalog.length > visibleCatalogCount;

    const setQty = (id: number, raw: string) => {
      setQuantities((prev) => ({ ...prev, [id]: raw.replace(/[^0-9]/g, '') }));
    };

    const handleToggleMaterial = (itemId: number) => {
      setSelectedMaterialIds((prev) => {
        const isSelected = prev.includes(itemId);
        if (isSelected) return prev.filter((id) => id !== itemId);
        if (prev.includes(itemId)) return prev;
        setQuantities((prevQty) => ({
          ...prevQty,
          [itemId]: prevQty[itemId] && prevQty[itemId].length > 0 ? prevQty[itemId] : '1',
        }));
        window.setTimeout(() => {
          const input = qtyInputRefs.current[itemId];
          input?.focus();
          input?.select();
        }, 0);
        return [...prev, itemId];
      });
    };

    const actionsToSubmit = [...new Set(selectedMaterialIds)]
      .map((id) => ({
        packaging_item_id: id,
        quantity: Math.max(1, parseInt(quantities[id] || '1', 10) || 1),
      }))
      .filter((a) => a.quantity > 0);

    const isConditionRowValid = (row: ConditionFormRow): boolean => {
      if (isBooleanField(row)) return true;
      return row.value.trim().length > 0;
    };

    const addConditionRow = () => {
      setConditionRows((prev) => [...prev, createConditionRow()]);
    };

    const removeCondition = (id: string) => {
      setConditionRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : prev));
    };

    const buildConditionFromRow = (row: ConditionFormRow, groupNo: number): CreateMarketingRuleConditionPayload => {
      const condition: CreateMarketingRuleConditionPayload = {
        field: row.field,
        operator: row.operator,
        value_type: isNumericField(row) ? 'number' : isBooleanField(row) ? 'boolean' : 'string',
        group_no: groupNo,
      };
      if (isNumericField(row)) condition.value_number = Number(row.value);
      else if (isBooleanField(row)) condition.value_boolean = row.boolValue;
      else condition.value_string = row.value;
      return condition;
    };

    const buildConditionLabel = (condition: CreateMarketingRuleConditionPayload): string => {
      const fLabel = fieldOptions.find((f) => f.value === condition.field)?.label ?? condition.field;
      if (condition.value_boolean !== undefined) {
        return `${fLabel}: ${condition.value_boolean ? t('specialRulesModal.boolean.true') : t('specialRulesModal.boolean.false')}`;
      }
      const opLabel = operatorOptions.find((o) => o.value === condition.operator)?.label ?? condition.operator;
      const v = condition.value_number !== undefined ? String(condition.value_number) : (condition.value_string ?? '');
      return `${fLabel} ${opLabel} ${v}`;
    };

    const buildConditionsSummary = (conds: CreateMarketingRuleConditionPayload[]): string | undefined => {
      if (conds.length === 0) return undefined;
      const groups = new Map<number, CreateMarketingRuleConditionPayload[]>();
      conds.forEach((c) => {
        const g = c.group_no ?? 0;
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g)!.push(c);
      });
      const groupLabels = Array.from(groups.values()).map((group) =>
        group.map(buildConditionLabel).join(` ${t('specialRulesModal.separatorAnd')} `),
      );
      return groupLabels.join(` ${t('specialRulesModal.separatorOr')} `);
    };

    const buildFinalConditions = (): CreateMarketingRuleConditionPayload[] => {
      let groupNo = 0;
      const result: CreateMarketingRuleConditionPayload[] = [];
      conditionRows.forEach((row, idx) => {
        if (!isConditionRowValid(row)) return;
        if (idx > 0 && row.joinWithPrevious === 'OR') groupNo += 1;
        result.push(buildConditionFromRow(row, groupNo));
      });
      return result;
    };

    const validConditionRowsCount = conditionRows.filter(isConditionRowValid).length;
    const canSubmitCondition = alwaysInclude
      ? true
      : validConditionRowsCount === conditionRows.length && conditionRows.length > 0;
    const canSubmit = Boolean(ruleId) && name.trim().length > 0 && actionsToSubmit.length > 0 && canSubmitCondition;

    const handleSave = async () => {
      if (!canSubmit || !ruleId) return;
      const finalConditions = !alwaysInclude ? buildFinalConditions() : [];
      const conditionSummary = buildConditionsSummary(finalConditions);

      try {
        await onSubmit({
          ruleId,
          name: name.trim(),
          priority,
          active: activeRule,
          isAlways: alwaysInclude,
          conditions: finalConditions,
          conditionSummary,
          actions: actionsToSubmit,
        });
        modalRef.current?.closeModal();
      } catch {
        // El padre maneja el toast de error.
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('marketing.editRuleModal.title')}
        type="default"
        width="lg"
        data-testid="edit-marketing-rule-modal"
      >
        <div className="flex h-[78vh] max-h-[78vh] flex-col">
          <div className="space-y-4 overflow-y-auto pr-1 flex-1" data-testid="edit-marketing-rule-modal-content">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">{t('marketing.editRuleModal.name')}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('marketing.ruleModal.namePlaceholder')}
                data-testid="edit-marketing-rule-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">{t('marketing.editRuleModal.priority')}</label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                data-testid="edit-marketing-rule-priority-input"
              />
            </div>

            <div className="flex items-start justify-between gap-3 rounded-md border border-card-border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text">{t('marketing.editRuleModal.active')}</p>
                <p className="text-xs text-subtext mt-0.5">{t('marketing.editRuleModal.activeHint')}</p>
              </div>
              <Switch
                checked={activeRule}
                onCheckedChange={setActiveRule}
                data-testid="edit-marketing-rule-active-switch"
              />
            </div>

            <div className="space-y-3 rounded-md border border-card-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text">{t('marketing.ruleModal.alwaysInclude')}</p>
                  <p className="text-xs text-subtext mt-0.5">
                    {alwaysInclude
                      ? t('marketing.ruleModal.alwaysIncludeHint')
                      : t('marketing.ruleModal.alwaysIncludeOffHint')}
                  </p>
                </div>
                <Switch
                  checked={alwaysInclude}
                  onCheckedChange={setAlwaysInclude}
                  data-testid="edit-marketing-rule-always-switch"
                />
              </div>

              {!alwaysInclude && (
                <div className="space-y-3 w-full">
                  {conditionRows.map((row, idx) => (
                    <div key={row.id} className="space-y-2 rounded-md border border-card-border p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-subtext">Condición {idx + 1}</p>
                        {conditionRows.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-subtext hover:text-error shrink-0"
                            onClick={() => removeCondition(row.id)}
                            aria-label={t('specialRulesModal.removeConditionAria')}
                            data-testid={`edit-marketing-rule-remove-condition-${idx}`}
                          >
                            <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>

                      {idx > 0 && (
                        <Combobox
                          options={[
                            { value: 'AND', label: t('specialRulesModal.separatorAnd') },
                            { value: 'OR', label: t('specialRulesModal.separatorOr') },
                          ]}
                          value={row.joinWithPrevious}
                          setValue={(v) =>
                            updateConditionRow(row.id, (current) => ({
                              ...current,
                              joinWithPrevious: v as 'AND' | 'OR',
                            }))
                          }
                          placeholder={t('specialRulesModal.separatorAnd')}
                          dataTestId={`edit-marketing-rule-join-${idx}`}
                        />
                      )}

                      <Combobox
                        options={fieldOptions}
                        value={row.field}
                        setValue={(newField) => handleFieldChange(row.id, newField)}
                        placeholder={t('specialRulesModal.fieldPlaceholder')}
                        dataTestId={`edit-marketing-rule-field-combobox-${idx}`}
                      />

                      {isBooleanField(row) ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Combobox
                            options={operatorOptions.filter((o) => ['=', '!='].includes(o.value))}
                            value={row.operator}
                            setValue={(v) =>
                              updateConditionRow(row.id, (current) => ({
                                ...current,
                                operator: v as PackagingRuleOperator,
                              }))
                            }
                            placeholder={t('specialRulesModal.operatorPlaceholder')}
                            dataTestId={`edit-marketing-rule-operator-combobox-${idx}`}
                          />
                          <Combobox
                            options={[
                              { value: 'true', label: t('specialRulesModal.boolean.true') },
                              { value: 'false', label: t('specialRulesModal.boolean.false') },
                            ]}
                            value={row.boolValue ? 'true' : 'false'}
                            setValue={(v) =>
                              updateConditionRow(row.id, (current) => ({
                                ...current,
                                boolValue: v === 'true',
                              }))
                            }
                            placeholder={t('specialRulesModal.valuePlaceholder')}
                            dataTestId={`edit-marketing-rule-value-boolean-${idx}`}
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <Combobox
                            options={
                              isNumericField(row)
                                ? operatorOptions.filter((o) => ['=', '!=', '>', '<', '>=', '<='].includes(o.value))
                                : operatorOptions.filter((o) => ['=', '!=', 'contains'].includes(o.value))
                            }
                            value={row.operator}
                            setValue={(v) =>
                              updateConditionRow(row.id, (current) => ({
                                ...current,
                                operator: v as PackagingRuleOperator,
                              }))
                            }
                            placeholder={t('specialRulesModal.operatorPlaceholder')}
                            dataTestId={`edit-marketing-rule-operator-combobox-${idx}`}
                          />
                          {isCountryField(row) ? (
                            <Combobox
                              options={countryOptions}
                              value={row.value}
                              setValue={(v) =>
                                updateConditionRow(row.id, (current) => ({
                                  ...current,
                                  value: v,
                                }))
                              }
                              placeholder={t('specialRulesModal.countryPlaceholder')}
                              dataTestId={`edit-marketing-rule-value-country-${idx}`}
                            />
                          ) : (
                            <Input
                              type={isNumericField(row) ? 'number' : 'text'}
                              value={row.value}
                              onChange={(e) =>
                                updateConditionRow(row.id, (current) => ({
                                  ...current,
                                  value: e.target.value,
                                }))
                              }
                              placeholder={t('specialRulesModal.valuePlaceholder')}
                              data-testid={`edit-marketing-rule-value-input-${idx}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addConditionRow}
                      data-testid="edit-marketing-rule-add-condition-button"
                    >
                      {t('specialRulesModal.addCondition')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1.5 pb-1">
              <label className="text-sm font-medium text-text">{t('marketing.ruleModal.selectMaterials')}</label>
              {catalog.length === 0 ? (
                <p className="text-sm text-subtext">{t('marketing.ruleModal.emptyCatalog')}</p>
              ) : (
                <>
                  <div className="w-full flex items-center gap-3">
                    <div className="relative flex-1 min-w-0">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-subtext"
                      />
                      <Input
                        value={catalogSearch}
                        onChange={(e) => {
                          setCatalogSearch(e.target.value);
                          setVisibleCatalogCount(6);
                        }}
                        placeholder={t('marketing.ruleModal.searchPlaceholder')}
                        data-testid="edit-marketing-catalog-search-input"
                        className="pl-9"
                      />
                    </div>
                    <div className="shrink-0">
                      <Sort
                        options={catalogSortOptions}
                        onSort={(data: SortCallbackData) => {
                          setHasCatalogSortSelection(true);
                          setCatalogSort(toSortValue(data.field, data.direction));
                          setVisibleCatalogCount(6);
                        }}
                        initialValue={hasCatalogSortSelection ? parseSortValue(catalogSort) : undefined}
                        defaultLabel="Ordenar"
                        dataTestId="edit-marketing-catalog-sort"
                        size="default"
                        className="h-10 w-full justify-between"
                      />
                    </div>
                  </div>

                  <p
                    className="pt-1 text-right text-xs text-subtext"
                    data-testid="edit-marketing-selected-materials-count"
                  >
                    {t('marketing.ruleModal.selectedCount', { count: selectedMaterialIds.length })}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[280px] overflow-y-auto pr-1">
                    {visibleCatalogItems.map((item) => {
                      const isSelected = selectedMaterialIds.includes(item.id);
                      const qty = quantities[item.id] || '1';
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            'rounded-lg border p-3',
                            isSelected ? 'border-primary bg-card-bg' : 'border-card-border',
                          )}
                          data-testid={`edit-marketing-modal-material-${item.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-auto flex-1 justify-start p-0 hover:bg-transparent text-left"
                              onClick={() => handleToggleMaterial(item.id)}
                              data-testid={`edit-marketing-modal-material-select-${item.id}`}
                            >
                              <div className="flex w-full items-center gap-3">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt=""
                                    className="h-9 w-9 flex-shrink-0 rounded-lg object-cover border border-card-border"
                                  />
                                ) : (
                                  <div
                                    className={cn(
                                      'h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center',
                                      item.colorClass,
                                    )}
                                  >
                                    <FontAwesomeIcon icon={faGift} className={cn('h-3.5 w-3.5', item.iconClass)} />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1 text-left">
                                  <p className="text-sm font-medium text-text truncate">{item.name}</p>
                                  <p className="text-xs text-subtext">{item.description}</p>
                                </div>
                              </div>
                            </Button>

                            {isSelected && (
                              <div className="ml-auto flex-shrink-0">
                                <Input
                                  className="h-8 w-16"
                                  value={qty}
                                  onChange={(e) => setQty(item.id, e.target.value)}
                                  inputMode="numeric"
                                  data-testid={`edit-marketing-modal-qty-${item.id}`}
                                  ref={(el) => {
                                    qtyInputRefs.current[item.id] = el;
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasMoreCatalogItems && (
                    <div className="flex justify-center mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setVisibleCatalogCount((prev) => prev + 6)}
                        data-testid="edit-marketing-catalog-load-more"
                      >
                        {t('marketing.ruleModal.loadMore')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-3 flex justify-end gap-2 border-t border-card-border pt-4">
            <Button
              variant="outline"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="edit-marketing-rule-cancel"
            >
              {t('marketing.editRuleModal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSubmit || Boolean(isSubmitting)}
              data-testid="edit-marketing-rule-save"
            >
              {isSubmitting ? t('marketing.editRuleModal.saving') : t('marketing.editRuleModal.save')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

EditMarketingRuleModal.displayName = 'EditMarketingRuleModal';
